/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import * as THREE from 'three';
// import { MathUtils } from 'three';
import { gsap } from "gsap";
import { MATERIAL_THICKNESS as MT, BLEED } from '@/box/constants.js'
import { CLIPPING_FIX_OFFSET } from './constants.js'

const HALF_MT = MT / 2;

/**
 * Calculates the width, height, and depth of a 3D object in Three.js.
 *
 * @param {THREE.Object3D} object - The Three.js object to measure.
 * @return {Object} An object containing the width, height, and depth of the input object.
 */
function getObjectDimensions(object) {
  const boundingBox = new THREE.Box3().setFromObject(object);

  const width = boundingBox.max.x - boundingBox.min.x;
  const height = boundingBox.max.y - boundingBox.min.y;
  const depth = boundingBox.max.z - boundingBox.min.z;

  return {
      width: width,
      height: height,
      depth: depth
  };
}

/**
 * Calculates the center point of the given object's bounding box.
 *
 * @param {THREE.Object3D} object - The object for which to calculate the center.
 * @returns {THREE.Vector3} The center point of the object's bounding box.
 */
function getObjectCenter(object) {
  const boundingBox = new THREE.Box3().setFromObject(object);
  const center = boundingBox.getCenter(new THREE.Vector3());

  return center;
}

function putDebugCube(parent, position) {
  const cubeGeo = new THREE.SphereGeometry(0.5, 4, 2);
  const cubeMat = new THREE.MeshStandardMaterial({
    wireframe: true,
    color: 0xFF0000,
  })
  const cubeMesh = new THREE.Mesh(cubeGeo, cubeMat);

  cubeMesh.position.copy(position);
  parent.add(cubeMesh);
}

function setPanelMaterials(group) {
  group.children.forEach(async (p) => {
    // if panelName is not defined or empty, skip
    if (!p.panelName) return;

    p.outsideMatIndex = 0
  });
}

// deg to rad
function deg2Rad(deg) {
  return deg * Math.PI / 180;
}

function foldPanels(box, group) {

  const {
    width: boxWidth,
    length: boxLength,
    depth: boxDepth
  } = box;

  const lidPivotPoint = new THREE.Vector3(0, 0, 0);
  const row5PivotPoint = new THREE.Vector3(0, 0, 0);

  group.children.forEach(async (p) => {

    // get panel dimensions (width, height, depth)
    let dim = getObjectDimensions(p)


    const skip = [
      // 'outsideFrontDustFlapLeft',
      // 'outsideFrontWall',
      // 'outsideFrontDustFlapRight',
      // 'insideLeftWall',
      // 'leftOverlap',
      // 'outsideLeftWall',
      // 'outsideBottom',
      // 'outsideRightWall',
      // 'rightOverlap',
      // 'insideRightWall',
      // 'outsideBackWallDustFlapLeft',
      // 'outsideBackWall',
      // 'outsideBackWallDustFlapRight',
      // 'leftDustFlapOutside',
      // 'topSide',
      // 'rightDustFlapOutside',
      // 'leftClosingFlap',
      // 'frontFlapOutside',
      // 'rightClosingFlap',
    ]
    // const pName = p.panelName.split(' ')[0]
    // if (skip.includes(pName)) return;

    // row 1

    if (p.panelName.includes('outsideFrontDustFlapLeft')) {

      // fold 1
      p.position.z += dim.depth / 2 + MT;
      p.position.y -= dim.depth / 2;
      p.rotation.x = Math.PI / 2;

      // fold 1 material thickness adjustment
      p.position.z += HALF_MT;

      // fold 2
      p.position.z += dim.width / 2;
      p.position.x += dim.width / 2;
      p.rotation.z = Math.PI / 2;

      // fold 2 material thickness adjustment
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET/2;
      p.position.z -= HALF_MT;
      p.position.y -= HALF_MT;
    }

    if (p.panelName.includes('outsideFrontDustFlapRight')) {

      // fold 1
      p.position.z += dim.depth / 2 + MT;
      p.position.y -= dim.depth / 2;
      p.rotation.x = Math.PI / 2;

      // fold 1 material thickness adjustment
      p.position.z += HALF_MT;

      // fold 2
      p.position.z += dim.width / 2;
      p.position.x -= dim.width / 2;
      p.rotation.z = -Math.PI / 2;

      // fold 2 material thickness adjustment
      p.position.x -= HALF_MT - CLIPPING_FIX_OFFSET/2;
      p.position.z -= HALF_MT;
      p.position.y -= HALF_MT;
    }

    if (p.panelName.includes('FrontWall')) {

      p.position.z += dim.depth / 2;
      p.position.y -= dim.depth / 2;
      p.rotation.x = -Math.PI / 2;

      // material thickness adjustment
      p.position.z += HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.y += HALF_MT - CLIPPING_FIX_OFFSET;
    }

    // row 2

    // Left

    if (p.panelName.includes('insideLeftWall')) {

      p.position.y -= dim.width / 2;
      p.position.x += dim.width / 2 + (8 * MT) + dim.width + BLEED - HALF_MT;
      p.rotation.z = Math.PI * 1.5; // -270 degrees

      p.position.x += HALF_MT + CLIPPING_FIX_OFFSET;
      p.position.y -= HALF_MT - CLIPPING_FIX_OFFSET;
    }

    if (p.panelName.includes('leftOverlap')) {

      p.position.y -= boxDepth + MT;
      p.position.x += (boxDepth + 2*MT) + (dim.width / 2) + (7*MT / 2) - MT;
      p.rotation.z = Math.PI; // -180 degrees

      // material thickness adjustment
      // TODO: Check why x2 is needed
      p.position.y -= CLIPPING_FIX_OFFSET * 2;
    }

    if (p.panelName.includes('outsideLeftWall')) {

      p.position.y -= (dim.width / 2);
      p.position.x += (boxDepth / 2 ) + (MT);
      p.rotation.z = Math.PI / 2; // -90 degrees

      // material thickness adjustment
      p.position.y += HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET;
    }

    // Right

    if (p.panelName.includes('insideRightWall')) {

      p.position.y -= dim.width / 2;
      p.position.x -= dim.width / 2 + (8 * MT) + dim.width + BLEED - HALF_MT;
      p.rotation.z = -Math.PI * 1.5;

      p.position.x -= HALF_MT + CLIPPING_FIX_OFFSET;
      p.position.y -= HALF_MT - CLIPPING_FIX_OFFSET;
    }

    if (p.panelName.includes('rightOverlap')) {

      p.position.y -= boxDepth + MT;
      p.position.x -= (boxDepth + 2*MT) + (dim.width / 2) + (7*MT / 2) - MT;
      p.rotation.z = Math.PI;

      // material thickness adjustment
      // TODO: Check why x2 is needed
      p.position.y -= CLIPPING_FIX_OFFSET * 2;
    }

    if (p.panelName.includes('outsideRightWall')) {

      p.position.y -= (dim.width / 2);
      p.position.x -= (boxDepth / 2 ) + (MT);
      p.rotation.z = -Math.PI / 2;

      // material thickness adjustment
      p.position.y += HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.x -= HALF_MT - CLIPPING_FIX_OFFSET;
    }

    // row 3

    if (p.panelName.includes('outsideBackWallDustFlapLeft')) {

      p.position.z -= dim.depth / 2 + MT;
      p.position.y -= dim.depth / 2;
      p.rotation.x = Math.PI / 2;

      // fold 1 material thickness adjustment
      p.position.z -= HALF_MT;

      // fold 2
      p.position.z -= dim.width / 2;
      p.position.x += dim.width / 2;
      p.rotation.z = Math.PI / 2;

      // fold 2 material thickness adjustment
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET/2;
    }

    if (p.panelName === 'outsideBackWall thirdRow') {

      p.position.y -= (dim.depth / 2);
      p.position.z -= (dim.depth / 2);
      p.rotation.x = Math.PI / 2;

      // material thickness adjustment
      p.position.y += HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.z -= HALF_MT - CLIPPING_FIX_OFFSET;

      lidPivotPoint.x = p.position.x;
      lidPivotPoint.y = p.position.y + (dim.depth / 2);
      lidPivotPoint.z = p.position.z + HALF_MT;
    }

    if (p.panelName.includes('outsideBackWallDustFlapRight')) {

      // fold 1
      p.position.z -= dim.depth / 2 + MT;
      p.position.y -= dim.depth / 2;
      p.rotation.x = Math.PI / 2;

      // fold 1 material thickness adjustment
      p.position.z -= HALF_MT;

      // fold 2
      p.position.z -= dim.width / 2;
      p.position.x -= dim.width / 2;
      p.rotation.z = -Math.PI / 2;

      // fold 2 material thickness adjustment
      p.position.x -= HALF_MT - CLIPPING_FIX_OFFSET/2;
    }

    // row 4

    if (p.panelName.includes('leftDustFlapOutside')) {

      // first fold
      p.rotation.z = Math.PI / 2;
      p.position.x -= -dim.width / 2;
      p.position.y += -dim.width / 2;

      // material thickness adjustment after first fold
      p.position.y -= HALF_MT + CLIPPING_FIX_OFFSET;
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET;

      // second fold
      p.rotation.x = Math.PI;
      p.position.z -= dim.depth + boxDepth + (MT * 6);
      // @TODO: Fix this
      p.position.y -= (MT * 2) - MT;

      // material thickness adjustment after second fold
      p.position.y += CLIPPING_FIX_OFFSET;
    }

    if (p.panelName.includes('rightDustFlapOutside')) {

      // first fold
      p.rotation.z = -Math.PI / 2;
      p.position.x += -dim.width / 2 - MT;
      p.position.y += -dim.width / 2;

      // material thickness adjustment after first fold
      p.position.y += CLIPPING_FIX_OFFSET;
      p.position.x += HALF_MT + CLIPPING_FIX_OFFSET;

      // second fold
      p.rotation.x = Math.PI;
      p.position.z -= dim.depth + boxDepth + (MT * 6);
      p.position.y -= MT;

      // material thickness adjustment after second fold
      p.position.y -= HALF_MT + CLIPPING_FIX_OFFSET;
    }

    if (p.panelName.includes('topSide')) {

      p.rotation.x = Math.PI;
      p.position.z -= dim.depth + boxDepth + (MT * 2)
      p.position.y -= boxDepth + MT

      // material thickness adjustment
      p.position.y -= CLIPPING_FIX_OFFSET * 2;

      p.outsideMatIndex = 1;

      row5PivotPoint.x = p.position.x;
      row5PivotPoint.y = p.position.y;
      row5PivotPoint.z = p.position.z - (boxWidth / 2);
    }

    // row 5

    if (p.panelName.includes('leftClosingFlap')) {

      // first fold
      p.rotation.z = Math.PI / 2;
      p.position.x -= -dim.width / 2;
      p.position.y += -dim.width / 2 + MT;

      // material thickness adjustment after first fold
      p.position.y -= HALF_MT;
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET;


      // // second fold
      p.rotation.x = deg2Rad(270);
      p.position.z -= dim.depth / 2 + boxWidth + boxDepth + boxWidth - (MT * 8);
      p.position.y = -dim.depth / 2 - (MT * 2);
    }

    if (p.panelName.includes('rightClosingFlap')) {

      // first fold
      p.rotation.z = -Math.PI / 2;
      p.position.x += -dim.width / 2;
      p.position.y += -dim.width / 2 + MT;

      // material thickness adjustment after first fold
      p.position.y -= HALF_MT;
      p.position.x -= HALF_MT - CLIPPING_FIX_OFFSET;

      // second fold
      p.rotation.x = deg2Rad(270);
      p.position.z -= dim.depth / 2 + boxWidth + boxDepth + boxWidth - (MT * 8);
      p.position.y = -dim.depth / 2 - (MT * 2);
    }

    if (p.panelName.includes('frontFlapOutside')) {

      p.rotation.x = -Math.PI / 2;
      p.position.z -= dim.depth / 2 + boxWidth + boxDepth + (MT * 7) + boxWidth + (MT * 2);
      // p.position.z -= dim.depth / 2;
      p.position.y -= dim.depth / 2 + MT;

      // material thickness adjustment
      p.position.y -= HALF_MT;
      p.position.z -= HALF_MT + CLIPPING_FIX_OFFSET;
    }

  });
}

function newFoldPanels(box, group) {

  // Get Box Width, Length, Depth
  const {
    width: W,
    length: L,
    depth: D
  } = box;
  // console.log('W:', W, 'L:', L, 'D:', D)

  // Get Box Guide

  const {
    x: guideX,
    y: guideY
  } = box.getGuidePoint()
  // console.log('guideX:', guideX, 'guideY:', guideY)

  const boxGuideObj = new THREE.Object3D();
  boxGuideObj.position.set(guideX, 0, guideY);
  group.add(boxGuideObj);

  // Visualize Guide Point
  const cubeGeo = new THREE.SphereGeometry(0.5, 4, 2);
  const cubeMat = new THREE.MeshStandardMaterial({
    wireframe: true,
    color: 0x0000FF,
  })
  const cubeMesh = new THREE.Mesh(cubeGeo, cubeMat);
  boxGuideObj.add(cubeMesh);

  let dim, bb, center;
  let pointNorth, pointSouth, pointWest, pointEast;

  // central panel
  const pBottom = group.children.find(c => c?.panelName.includes('outsideBottom'));
  dim = getObjectDimensions(pBottom)
  bb = new THREE.Box3().setFromObject(pBottom);
  center = new THREE.Vector3();
  bb.getCenter(center);

  pointWest = new THREE.Vector3();
  pointWest.x -= dim.width / 2;
  // console.log('pointWest', pointWest)
  putDebugCube(pBottom, pointWest)

  pointEast = new THREE.Vector3();
  pointEast.x += dim.width / 2;
  // console.log('pointEast', pointEast)
  putDebugCube(pBottom, pointEast)

  pointNorth = new THREE.Vector3();
  pointNorth.z -= dim.depth / 2 - MT;
  // console.log('pointNorth', pointNorth)
  putDebugCube(pBottom, pointNorth)

  pointSouth = new THREE.Vector3();
  pointSouth.z += dim.depth / 2 - MT;
  // console.log('pointSouth', pointSouth)
  putDebugCube(pBottom, pointSouth)

  // front panel
  const pFront = group.children.find(c => c?.panelName.includes('FrontWall'));
  dim = getObjectDimensions(pFront)
  bb = new THREE.Box3().setFromObject(pFront);
  center = new THREE.Vector3();
  bb.getCenter(center);

  pointWest = new THREE.Vector3();
  pointWest.x -= dim.width / 2;
  // console.log('pointWest', pointWest)
  putDebugCube(pFront, pointWest)

  pointEast = new THREE.Vector3();
  pointEast.x += dim.width / 2;
  // console.log('pointEast', pointEast)
  putDebugCube(pFront, pointEast)



}

function initLidAnimation(group, scene) {
  const tl = gsap.timeline({ paused: true });

  const lidPivotPoint = new THREE.Object3D();
  const row5PivotPoint = new THREE.Object3D();

  const topSide = group.children.find(c => c?.panelName.includes('topSide'));
  const leftDustFlapOutside = group.children.find(c => c?.panelName.includes('leftDustFlapOutside'));
  const rightDustFlapOutside = group.children.find(c => c?.panelName.includes('rightDustFlapOutside'));

  const topSideDim = getObjectDimensions(topSide)

  const frontFlapOutside = group.children.find(c => c?.panelName.includes('frontFlapOutside'));
  const leftClosingFlap = group.children.find(c => c?.panelName.includes('leftClosingFlap'));
  const rightClosingFlap = group.children.find(c => c?.panelName.includes('rightClosingFlap'));

  const frontFlapOutsideDim = getObjectDimensions(frontFlapOutside)

  // LID PREPARATION
  // ------------------------------
  lidPivotPoint.closedLidRotation = 0;
  lidPivotPoint.openLidRotation = -(Math.PI / 4) * 3;

  const originalTopSidePosition = topSide.position.clone();
  lidPivotPoint.position.copy(originalTopSidePosition);
  lidPivotPoint.position.z += topSideDim.depth / 2

  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, leftDustFlapOutside);
  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, rightDustFlapOutside);
  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, topSide);
  // ------------------------------

  // ROW 5 PREPARATION
  // ------------------------------
  row5PivotPoint.closedLidRotation = 0;
  row5PivotPoint.openLidRotation = -(Math.PI / 4) * 3;

  const originalFrontFlapOutsidePosition = frontFlapOutside.position.clone();
  row5PivotPoint.position.copy(originalFrontFlapOutsidePosition);
  row5PivotPoint.position.y -= frontFlapOutsideDim.height / 2

  addObjectToPivotPointAndAdjustPosition(row5PivotPoint, leftClosingFlap);
  addObjectToPivotPointAndAdjustPosition(row5PivotPoint, rightClosingFlap);
  addObjectToPivotPointAndAdjustPosition(row5PivotPoint, frontFlapOutside);

  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, row5PivotPoint);
  // ------------------------------


  group.add(lidPivotPoint);

  tl.to(row5PivotPoint.rotation, {x: row5PivotPoint.openLidRotation, duration: 0.8, ease: 'power1.inOut'})
  .to(lidPivotPoint.rotation, {x: lidPivotPoint.openLidRotation, duration: 1, ease: 'power1.inOut'}, 0.4)

  return tl
}

function addObjectToPivotPointAndAdjustPosition(pivotPoint, panel) {
  const originalPanelPosition = panel.position.clone();

  pivotPoint.add(panel);

  const newPanelPosition = originalPanelPosition.sub(pivotPoint.position)
  panel.position.copy(newPanelPosition);
}

export default function useMailingBoxTLModel(box, group, lidTimeline, scene) {
  
  // fold panels into a box
  foldPanels(box, group);
  // newFoldPanels(box, group);

  setPanelMaterials(group);

  lidTimeline.value = initLidAnimation(group, scene);
  
  scene.add(group);
}
